import {
  isCounterpartyScopedRole,
  isDepartmentScopedRole,
  isObjectScopedRole,
  isPersonScopedRole,
  type Role,
} from './enums';
import { can, PERMISSIONS, type AccessSubject, type Permission } from './permissions';

/**
 * Витринная часть модели доступа (`docs/permissions-tab-plan.md`): как назвать право человеку, к
 * какому модулю портала оно относится и что именно разрешает делать.
 *
 * Отдельным файлом от матрицы намеренно. `permissions.ts` отвечает на вопрос «кому», и читают его
 * ради этого; каталог отвечает «как это показать», и смешанные в одном файле, два ответа заставили
 * бы перечитывать двести строк обоснований ради подписи. Зависимость односторонняя: каталог знает
 * про матрицу, матрица про каталог — нет.
 *
 * `Record` по всем правам, а не список: новое право в `PERMISSIONS` обязано получить подпись и
 * модуль, иначе не соберётся сборка. Молча пропущенное право означало бы вкладку «Права», которая
 * о нём не рассказывает, — то есть витрину, врущую ровно там, где по ней принимают решение.
 */

/**
 * Модули портала — так, как их видит человек, а не так, как разложены маршруты. Совпадают с
 * разделами меню всюду, где раздел есть; там, где права живут поперёк разделов (архив, файлы,
 * администрирование), модуль называет предмет, а не страницу.
 */
export const PERMISSION_MODULES = [
  'directories',
  'drivers',
  'waybills',
  'waste',
  'vehicle',
  'weekly',
  /**
   * Механизация (план `docs/mechanization-module-plan.md`, Р9) — свой модуль витрины, а не строки
   * внутри «Заказа ТС»: у аренды малой механизации свой раздел, свой цикл и своя область, а
   * общая с техникой строка сказала бы держателю, что заказ самосвала и аренда виброплиты
   * открываются одним доступом.
   */
  'mech',
  'service',
  'officeEquipment',
  'garage',
  'driverCabinet',
  'vehicleReadings',
  /**
   * Техобслуживание — свой модуль витрины, а не пара прав внутри «Показаний техники» (Р14). Модуль
   * закрывается своим чтением, и лежи эти права в чужом, витрина рассказывала бы, что журнал ТО
   * открывается доступом к показаниям, — то есть ровно то смешение, которого решение избегает.
   */
  'vehicleMaintenance',
  'records',
  'files',
  /**
   * Руководства — свой модуль витрины, а не место в «Администрировании» (`docs/manuals-plan.md`
   * §3.1). Витрина считает недостающее «чтение модуля» по `MODULE_ENTRY_PERMISSION`, а у модуля
   * `admin` входное право — `users.manage`, то есть право из `NON_GRANTABLE_PERMISSIONS`. Лежи
   * `manuals.manage` там, конструктор полномочий на каждом наборе с ним показывал бы
   * предупреждение «чтения модуля в наборе нет», закрыть которое нечем: невыдаваемое право в
   * набор не положить. Прецедент модуля из одного права — `files`.
   */
  'manuals',
  /**
   * Сводная аналитика (план `docs/analytics-summary-export-plan.md`, Р2) — свой модуль витрины из
   * одного права, прецедент тот же, что у «Руководств» и «Файлов». В «Администрировании» ему места
   * нет по той же причине: у модуля `admin` входное право `users.manage` из
   * `NON_GRANTABLE_PERMISSIONS`, и набор с одной аналитикой показывал бы держателю
   * «чтения модуля в наборе нет», закрыть которое нечем. Внутрь сведённых модулей его тоже не
   * положить: приписанный к вывозу, он рассказывал бы, что человек работает в вывозе мусора.
   */
  'analytics',
  'admin',
] as const;
export type PermissionModule = (typeof PERMISSION_MODULES)[number];

export const permissionModuleLabels: Record<PermissionModule, string> = {
  directories: 'Справочники',
  drivers: 'Водители',
  waybills: 'Путевые листы',
  waste: 'Вывоз мусора',
  vehicle: 'Заказ ТС',
  weekly: 'Недельная заявка',
  mech: 'Механизация',
  service: 'Орг.техника: заявки',
  officeEquipment: 'Орг.техника: справочник',
  garage: 'Гараж',
  driverCabinet: 'Кабинет водителя',
  vehicleReadings: 'Показания техники',
  vehicleMaintenance: 'Техобслуживание',
  records: 'Архив и откаты',
  files: 'Файлы',
  manuals: 'Руководства',
  analytics: 'Аналитика',
  admin: 'Администрирование',
};

/**
 * Вид действия — колонки сетки «модуль × действие». Их семь, и седьмое (`manage`) собирает то, что
 * в четыре первых не укладывается: назначение исполнителя, аннулирование бланка, выгрузку
 * справочника. Разводить каждый такой случай в свою колонку значило бы получить сетку из двадцати
 * пустых столбцов ради трёх заполненных клеток.
 */
export const PERMISSION_ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'status',
  'approve',
  'manage',
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export const permissionActionLabels: Record<PermissionAction, string> = {
  read: 'Чтение',
  create: 'Создание',
  update: 'Правка',
  delete: 'Удаление',
  status: 'Ход',
  approve: 'Согласование',
  manage: 'Особые',
};

export interface PermissionCatalogEntry {
  module: PermissionModule;
  action: PermissionAction;
  /**
   * Подпись глаголом от лица учётки: «Заводит заявку», а не «Создание заявки». Витрину читают
   * строкой «что человек может», и отглагольное существительное в этой строке спотыкается.
   */
  label: string;
}

export const PERMISSION_CATALOG: Record<Permission, PermissionCatalogEntry> = {
  'directories.read': {
    module: 'directories',
    action: 'read',
    label: 'Читает справочники',
  },
  'directories.write': { module: 'directories', action: 'update', label: 'Ведёт справочники' },
  'directories.export': {
    module: 'directories',
    action: 'manage',
    label: 'Выгружает справочник в файл',
  },
  'directories.import': {
    module: 'directories',
    action: 'manage',
    label: 'Загружает справочник из файла',
  },

  'wasteRequests.read': { module: 'waste', action: 'read', label: 'Видит заявки на вывоз' },
  'wasteRequests.create': { module: 'waste', action: 'create', label: 'Заводит заявку на вывоз' },
  'wasteRequests.update': { module: 'waste', action: 'update', label: 'Правит заявку на вывоз' },
  'wasteRequests.delete': { module: 'waste', action: 'delete', label: 'Удаляет заявку на вывоз' },
  'wasteRequests.status': {
    module: 'waste',
    action: 'status',
    label: 'Двигает заявку на вывоз по статусам',
  },
  'wasteRequests.assignOperator': {
    module: 'waste',
    action: 'manage',
    label: 'Назначает исполнителя вывоза',
  },
  'wasteRequests.operatorComment': {
    module: 'waste',
    action: 'manage',
    label: 'Пишет примечание исполнителя',
  },
  /*
   * Разбор талонов (ADR 0114). Действие `manage`, а не `status`: колонка «Ход» описывает движение
   * заявки по статусам, а разбор её статуса не меняет вовсе — замечания закрытие не блокируют
   * (Р23). Попади право в «Ход», витрина сказала бы, что сверка это часть закрытия заявки, — то
   * есть ровно то смешение с `wasteRequests.status`, ради которого право и заведено отдельным.
   */
  'wasteRequests.ticketReview': {
    module: 'waste',
    action: 'manage',
    label: 'Разбирает талоны вывоза',
  },
  /*
   * Аудит распознавания (ADR 0137, план аудита §4.1). Действие `read`, а не `manage`: все шесть
   * ручек только читают — сводка, когорты, споры, лупа на скан и выгрузка того же самого в CSV, —
   * и `moduleAccess` обязан показать держателя смотрящим, а не работающим в модуле. Выгрузка
   * решения не меняет: она уносит ровно то, что право и так показывает на экране, в отличие от
   * `directories.export`, где выгрузка и есть всё право, а уносит она справочник целиком.
   *
   * Вторая строка чтения в модуле — не задвоение входа: `wasteRequests.read` открывает заявки, это
   * право — журнал их разбора, и читаются они порознь (журнал переживает и талон, и заявку).
   * Входное право у модуля по-прежнему одно (`MODULE_ENTRY_PERMISSION.waste`), и аудит его
   * требует — строкой в `PERMISSION_REQUIRES`, а не тем, что стоит с ним в одной колонке.
   */
  'wasteRequests.ticketAudit': {
    module: 'waste',
    action: 'read',
    label: 'Смотрит аудит распознавания талонов',
  },

  'vehicleRequests.read': { module: 'vehicle', action: 'read', label: 'Видит заказы техники' },
  'vehicleRequests.create': { module: 'vehicle', action: 'create', label: 'Заводит заказ техники' },
  'vehicleRequests.update': { module: 'vehicle', action: 'update', label: 'Правит заказ техники' },
  'vehicleRequests.delete': { module: 'vehicle', action: 'delete', label: 'Удаляет заказ техники' },
  'vehicleRequests.status': {
    module: 'vehicle',
    action: 'status',
    label: 'Двигает заказ по статусам',
  },
  'vehicleRequests.approve': {
    module: 'vehicle',
    action: 'approve',
    label: 'Визирует заказ техники',
  },

  'weeklyRequests.read': { module: 'weekly', action: 'read', label: 'Видит недельные заявки' },
  'weeklyRequests.create': {
    module: 'weekly',
    action: 'create',
    label: 'Заводит недельную заявку',
  },
  'weeklyRequests.update': { module: 'weekly', action: 'update', label: 'Правит состав недели' },
  'weeklyRequests.approve': {
    module: 'weekly',
    action: 'approve',
    label: 'Визирует недельную заявку',
  },

  'mechRequests.read': { module: 'mech', action: 'read', label: 'Видит заявки на механизацию' },
  'mechRequests.create': {
    module: 'mech',
    action: 'create',
    label: 'Заводит заявку на механизацию',
  },
  'mechRequests.update': {
    module: 'mech',
    action: 'update',
    label: 'Правит заявку на механизацию',
  },
  'mechRequests.delete': {
    module: 'mech',
    action: 'delete',
    label: 'Удаляет заявку на механизацию',
  },
  /*
   * Ход аренды: договорённость с арендодателем, отметка выдачи и её снятие, завершение с фактом.
   * Действие `status`, потому что все они двигают заявку по её же циклу (Р2) — состояние аренды
   * различается полями, а не четвёртым статусом.
   */
  'mechRequests.status': {
    module: 'mech',
    action: 'status',
    label: 'Ведёт аренду механизации: договорённость, выдача, завершение',
  },
  /*
   * Продление — «Особые», а не «Ход»: срок аренды не двигает заявку по циклу вовсе, а означает
   * согласие платить дальше (Р9). Попади право в «Ход», витрина сказала бы, что продлевает всякий,
   * кто ведёт аренду, — то есть ровно то смешение с `.status`, ради которого право и разведено.
   */
  /*
   * Since the extension visa (ADR 0171, stage 3) the holder asks for an extension and the site signs
   * it; the label says so. Until release R2 the same permission still opens the old immediate door
   * for tabs of older bundles — the portal itself no longer offers it.
   */
  'mechRequests.extend': {
    module: 'mech',
    action: 'manage',
    label: 'Запрашивает продление аренды механизации',
  },
  /*
   * Виза площадки — своё действие витрины, как у заказа ТС и недельной заявки: подпись не входит
   * ни в ведение заявки, ни в ход аренды, и человек, читающий матрицу, обязан видеть её строкой.
   */
  'mechRequests.approve': {
    module: 'mech',
    action: 'approve',
    label: 'Визирует аренду механизации и её продление',
  },

  'drivers.read': { module: 'drivers', action: 'read', label: 'Читает карточки водителей' },
  'drivers.write': { module: 'drivers', action: 'update', label: 'Ведёт справочник водителей' },

  'waybills.read': { module: 'waybills', action: 'read', label: 'Читает журнал путевых листов' },
  'waybills.cancel': { module: 'waybills', action: 'manage', label: 'Аннулирует путевой лист' },
  'waybills.files': { module: 'waybills', action: 'manage', label: 'Подшивает документы к листу' },
  'waybills.issueBlank': {
    module: 'waybills',
    action: 'manage',
    label: 'Выписывает лист без заявок',
  },
  'waybills.correct': {
    module: 'waybills',
    action: 'manage',
    label: 'Исправляет документы задним числом',
  },
  'waybills.correctBeyondLimit': {
    module: 'waybills',
    action: 'manage',
    label: 'Исправляет задним числом старше 30 дней',
  },

  'serviceRequests.read': {
    module: 'service',
    action: 'read',
    label: 'Видит заявки на обслуживание',
  },
  /**
   * Вид действия — «Чтение», как у строки выше, и это не дубль клетки: право не открывает ручек, а
   * расширяет ответ уже открытых (план `docs/office-equipment-requester-card-plan.md`, Р1). В
   * «Особых» оно читалось бы как отдельная работа, тогда как это оговорка к чтению — тот же список
   * и та же карточка, только с деньгами.
   */
  'serviceRequests.finance': {
    module: 'service',
    action: 'read',
    label: 'Видит деньги и объём работ заявок',
  },
  'serviceRequests.create': {
    module: 'service',
    action: 'create',
    label: 'Заводит заявку на обслуживание',
  },
  /*
   * Вид действия — «Создание», как у соседней строки, а не «Особые» (план расходников и закупки,
   * Р5). Две клетки в одной колонке сетку не ломают — так же стоят четыре права в «Особых»
   * модуля, — а вот в «Особых» право читалось бы как отдельная работа, тогда как это оговорка к
   * заведению: та же ручка, тот же страж, разрешено лишь оставить аппарат пустым.
   */
  'serviceRequests.createWithoutEquipment': {
    module: 'service',
    action: 'create',
    label: 'Заводит заявку без аппарата',
  },
  'serviceRequests.update': {
    module: 'service',
    action: 'update',
    label: 'Правит заявку на обслуживание',
  },
  'serviceRequests.delete': {
    module: 'service',
    action: 'delete',
    label: 'Удаляет заявку на обслуживание',
  },
  'serviceRequests.assign': {
    module: 'service',
    action: 'manage',
    label: 'Назначает сервисную компанию',
  },
  'serviceRequests.estimate': { module: 'service', action: 'manage', label: 'Ведёт смету ремонта' },
  /*
   * Раскладка свободной записи по графам (план
   * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2). Действие `manage`, а не
   * `update`: колонка «Правка» модуля описывает правку самой заявки (`serviceRequests.update`), и
   * раскладка, попав в неё, читалась бы как её половина — тогда как это работа над другим
   * документом и с другими последствиями (снятая подпись, новая ревизия). Место ей рядом с
   * ведением сметы, строкой выше.
   */
  'serviceRequests.estimateRewrite': {
    module: 'service',
    action: 'manage',
    label: 'Переписывает объём работ по графам',
  },
  'serviceRequests.approveEstimate': {
    module: 'service',
    action: 'approve',
    label: 'Согласует смету ремонта',
  },
  'serviceRequests.approveIt': {
    module: 'service',
    action: 'approve',
    label: 'Визирует заявку от отдела ИТ',
  },
  'serviceRequests.status': {
    module: 'service',
    action: 'status',
    label: 'Двигает заявку на обслуживание по статусам',
  },
  'serviceRequests.hold': {
    module: 'service',
    action: 'status',
    label: 'Откладывает заявку и возобновляет',
  },
  // Action `status`, like the hold above it: a cancel is a move along the statuses too, and in the
  // showcase it reads next to it and not among the «Особые».
  'serviceRequests.cancel': {
    module: 'service',
    action: 'status',
    label: 'Отменяет заявку на обслуживание',
  },
  'serviceRequests.urgency': {
    module: 'service',
    action: 'manage',
    label: 'Помечает заявку срочной',
  },
  /**
   * Подпись говорит про работу, а не про клетку в форме назначения, и это не вольность. Право —
   * половина условия: ходы заявки открывает назначение, и держатель права без назначения не может
   * ничего. «Может быть назначен исполнителем» в витрине читалось бы как разрешение назначать —
   * то есть как `serviceRequests.assign`, которое стоит строкой выше и означает обратное.
   */
  'serviceRequests.execute': {
    module: 'service',
    action: 'manage',
    label: 'Работает исполнителем назначенных заявок',
  },
  'serviceRequests.files': {
    module: 'service',
    action: 'manage',
    label: 'Подшивает документы к заявке',
  },

  'officeEquipment.read': {
    module: 'officeEquipment',
    action: 'read',
    label: 'Читает справочник оргтехники',
  },
  'officeEquipment.write': {
    module: 'officeEquipment',
    action: 'update',
    label: 'Ведёт справочник оргтехники',
  },
  /*
   * Действие `manage`, а не `update`: колонка «Правка» описывает ведение справочника, а переезд —
   * событие со своей датой, причиной и обеими сторонами, и записывается он ручкой, которой правка
   * карточки объект не меняет вовсе (`objectId: z.never()`). Попади право в «Правку», витрина
   * сказала бы, что перемещение — часть ведения парка, то есть ровно то смешение, ради разделения
   * которого право и заведено (план перемещения из заявки, Р1).
   */
  'officeEquipment.move': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Подтверждает перемещение техники',
  },
  /*
   * Подпись говорит про сообщение, а не про заведение карточки, и это не смягчение формулировки:
   * держатель права в справочник не пишет вовсе — он отправляет кандидата, а карточку по нему
   * заводит проверяющий (план кандидатов, Р8). «Заводит технику» в витрине читалось бы как
   * `officeEquipment.write`, то есть как право, которого здесь намеренно нет.
   */
  'officeEquipment.propose': {
    module: 'officeEquipment',
    action: 'create',
    label: 'Сообщает о технике, которой нет в справочнике',
  },
  'officeEquipment.review': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Проверяет сообщения об отсутствующей технике',
  },
  'officeEquipment.telemetry': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Разбирает письма от аппаратов',
  },
  'officeEquipmentConsumables.manage': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Ведёт номенклатуру картриджей и тонеров',
  },
  'officeEquipmentConsumables.stock': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Правит остаток расходника вручную',
  },
  /*
   * Модуль витрины — тот же, что у оргтехники, а не свой (план расходников и закупки, Р12).
   * Закупка живёт вкладкой раздела «Орг.техника» и собирается из его же номенклатуры; свой модуль
   * означал бы своё входное право (`MODULE_ENTRY_PERMISSION`), а входа у закупки нет — она
   * открывается тем же `officeEquipment.read`, который ей и объявлен требованием.
   */
  'officeEquipmentPurchases.manage': {
    module: 'officeEquipment',
    action: 'manage',
    label: 'Ведёт плановые закупки расходников',
  },

  'garage.read': { module: 'garage', action: 'read', label: 'Смотрит день гаража' },
  /*
   * One permission covers the complete receipt-backed workflow: receipt entry, explicit stock
   * destination, and application documents. The stock balance itself is derived and has no edit
   * action, so reviving the retired `autoParts.stock` permission would describe no route.
   */
  'autoParts.manage': {
    module: 'garage',
    action: 'manage',
    label: 'Ведёт чеки и склад автозапчастей',
  },
  /*
   * Удаление, а не «ведение», и подпись обязана это показывать (план чеков, Р4а). Держатели
   * `autoParts.manage` чек только помечают на удаление — документ остаётся на месте, и пометку
   * снимают тем же правом; здесь запись уходит насовсем вместе со строками и сканами. Подпись
   * «Удаляет чек… насовсем» рядом с «Ведёт чеки…» читается как другое действие с первого взгляда,
   * а «Управляет чеками» слилась бы с ведением. Читают эту подпись двое: карточка доступа учётки
   * («что человек может») и отказ выдачи — `validateGrantAssignment` называет право именно ею,
   * когда объясняет, что оно не выдаётся полномочиями ни при каких условиях. Колонка витрины
   * поэтому `delete`, как у `records.purge`, а не `manage`: удаление стоит в своём столбце сетки,
   * а не среди «особых».
   */
  'autoParts.delete': {
    module: 'garage',
    action: 'delete',
    label: 'Удаляет чек на автозапчасти насовсем',
  },
  'driverCabinet.read': {
    module: 'driverCabinet',
    action: 'read',
    label: 'Открывает своё задание на дату',
  },
  'driverCabinet.submit': {
    module: 'driverCabinet',
    action: 'create',
    label: 'Передаёт свои показания',
  },
  'vehicleReadings.read': {
    module: 'vehicleReadings',
    action: 'read',
    label: 'Смотрит показания парка',
  },
  'vehicleReadings.write': {
    module: 'vehicleReadings',
    action: 'manage',
    label: 'Правит показания и принимает день',
  },
  /*
   * Действие `read`, а не `manage`: держатель ничего не меняет — он уносит книгу. `manage` объявил
   * бы модуль пишущим у того, кто в нём только читает, и витрина прав показала бы «работает» там,
   * где человек смотрит и выгружает.
   */
  'vehicleReadings.export': {
    module: 'vehicleReadings',
    action: 'read',
    label: 'Выгружает служебную книгу показаний',
  },
  /*
   * Аналитика живёт своим модулем витрины, а не внутри одного из трёх сведённых: приписанная,
   * скажем, к вывозу, она показала бы «работает в вывозе мусора» тому, кто в модуле не нажимает
   * ничего. Действие `read` по той же причине, что у соседа выше: держатель уносит книгу, а не
   * меняет данные.
   */
  'analytics.export': {
    module: 'analytics',
    action: 'read',
    label: 'Выгружает сводную аналитику по заказчикам',
  },
  'vehicleMaintenance.read': {
    module: 'vehicleMaintenance',
    action: 'read',
    label: 'Смотрит журнал ТО техники',
  },
  'vehicleMaintenance.write': {
    module: 'vehicleMaintenance',
    action: 'update',
    label: 'Ведёт журнал ТО техники',
  },

  'archive.read': { module: 'records', action: 'read', label: 'Видит удалённые записи' },
  'archive.restore': { module: 'records', action: 'manage', label: 'Возвращает запись из архива' },
  'requests.rollbackStatus': {
    module: 'records',
    action: 'status',
    label: 'Откатывает статус заявки',
  },
  'records.purge': { module: 'records', action: 'delete', label: 'Удаляет запись насовсем' },

  'files.manageAny': { module: 'files', action: 'manage', label: 'Удаляет чужие файлы' },
  /*
   * Карантин файла (план освобождения от подписи, Р6, п. 4). Действие `manage`, а не `read`, хотя
   * держатель прежде всего смотрит: право не только открывает содержимое, но и ставит карантин и
   * снимает его — то есть меняет доступность документа всем остальным. Покажи витрина «чтение», и
   * держатель выглядел бы наблюдателем, а он единственный, кто может запереть основание денежного
   * решения. Вторая строка `manage` в модуле — не задвоение: `files.manageAny` удаляет ничей файл,
   * это право прячет любой, и выдаются они порознь.
   */
  'files.quarantineAudit': {
    module: 'files',
    action: 'manage',
    label: 'Разбирает карантин файлов',
  },

  'users.manage': { module: 'admin', action: 'manage', label: 'Ведёт учётные записи' },
  'audit.read': { module: 'admin', action: 'read', label: 'Читает журнал действий' },
  'mailings.read': { module: 'admin', action: 'read', label: 'Смотрит рассылки' },
  'mailings.manage': { module: 'admin', action: 'manage', label: 'Настраивает рассылки' },

  'manuals.manage': { module: 'manuals', action: 'manage', label: 'Ведёт список руководств' },
};

/** Права модуля в порядке объявления — строки сетки собираются по нему, а не по алфавиту. */
export const PERMISSIONS_BY_MODULE: Record<PermissionModule, readonly Permission[]> =
  PERMISSION_MODULES.reduce(
    (acc, module) => {
      acc[module] = PERMISSIONS.filter((p) => PERMISSION_CATALOG[p].module === module);
      return acc;
    },
    {} as Record<PermissionModule, Permission[]>,
  );

/**
 * Право, которым модуль открывается целиком. Модули закрываются чтением (ADR 0021): нет его — нет
 * и раздела, а остальные права в нём недостижимы. У «Файлов» своего чтения нет — модуль состоит из
 * одного административного права, и открывающим считается оно.
 */
export const MODULE_ENTRY_PERMISSION: Record<PermissionModule, Permission> = {
  directories: 'directories.read',
  drivers: 'drivers.read',
  waybills: 'waybills.read',
  waste: 'wasteRequests.read',
  vehicle: 'vehicleRequests.read',
  weekly: 'weeklyRequests.read',
  mech: 'mechRequests.read',
  service: 'serviceRequests.read',
  officeEquipment: 'officeEquipment.read',
  garage: 'garage.read',
  driverCabinet: 'driverCabinet.read',
  vehicleReadings: 'vehicleReadings.read',
  vehicleMaintenance: 'vehicleMaintenance.read',
  records: 'archive.read',
  files: 'files.manageAny',
  // У «Руководств», как и у «Файлов», своего чтения нет: список читают все вошедшие, а модуль
  // состоит из одного права ведения — открывающим считается оно (`docs/manuals-plan.md` §3.1).
  manuals: 'manuals.manage',
  // Модуль из одного права: открывающим считается оно само — как у «Файлов» и «Руководств».
  analytics: 'analytics.export',
  admin: 'users.manage',
};

/**
 * Права, каждое из которых открывает хотя бы одну вкладку «Администрирования»
 * (`docs/manuals-plan.md` §3.6).
 *
 * Список один, потому что мест, спрашивающих «пускать ли на `/admin`», четыре — маршрут, набор
 * вкладок, пункт меню и стартовый редирект, — и сегодня они знают разное. Расхождение уже стоит
 * двух дыр, обе из каталога полномочий (ADR 0106): держатель набора «Рассылки» проходит маршрут,
 * но пункта меню не видит и редиректом на страницу не попадает, а держатель «Обмена
 * справочниками» не попадает вовсе — вкладка для него в коде есть, а маршрут его разворачивает.
 * Четвёртая поимённая строка про `manuals.manage` в трёх местах завела бы третью дыру того же
 * рода.
 *
 * Список отвечает только на вопрос «пускать ли на страницу». Сами вкладки остаются поимёнными: у
 * каждой своё право, и «что показать» — другой вопрос, чем «пустить ли». Что список и вкладки не
 * разъедутся, следит тест равенством множеств.
 *
 * Права здесь, а не модули: `MODULE_ENTRY_PERMISSION.admin` — это `users.manage`, то есть ровно
 * то право, мимо которого держатели двух наборов и не проходят.
 */
export const ADMIN_PAGE_PERMISSIONS = [
  'users.manage',
  'mailings.read',
  'directories.export',
  'manuals.manage',
  // The readings report (`vehicleReadings.export`) is deliberately absent: its home is the garage
  // header (ADR 0241), and the manager and both mechanic roles hold it by role — listed here, it
  // would give them «Администрирование» with a single tab. The book stays in «Выгрузки» for those
  // the page admits by another right.
  // Сводная аналитика — вторая выгрузка той же вкладки (план, Р1): без строки здесь раздел не
  // открылся бы тому, у кого из административных прав есть только она.
  'analytics.export',
] as const satisfies readonly Permission[];

/**
 * Что субъект делает в модуле: не видит вовсе, только смотрит или ещё и действует. Три состояния,
 * а не флаг: «видит» и «работает» — разные ответы, и в витрине их путать нельзя.
 */
export type ModuleAccess = 'none' | 'read' | 'write';

export function moduleAccess(
  subject: AccessSubject | null | undefined,
  module: PermissionModule,
): ModuleAccess {
  const granted = PERMISSIONS_BY_MODULE[module].filter((p) => can(subject, p));
  if (granted.length === 0) return 'none';
  return granted.some((p) => PERMISSION_CATALOG[p].action !== 'read') ? 'write' : 'read';
}

/**
 * Ограничения области субъекта — словами (ADR 0021 §5, ADR 0039, 0040, 0062, 0070, 0102).
 *
 * Это витрина второго слоя доступа, который живёт в `apps/api/src/lib/access.ts`, и повторять его
 * механику здесь нечем: правило — текст, а не предикат по строке. Чтобы витрина не разошлась с
 * моделью молча, применимость правил задана теми же предикатами ролей, что и сам слой
 * (`isObjectScopedRole` и соседние), а полнота набора закреплена тестом: роль с областью обязана
 * получить хотя бы одно правило.
 *
 * ПРЕДИКАТ ЗНАЕТ РОЛЬ, НО НЕ ЕЁ НАБОР, и это не недосмотр `applies`, а положение витрины: площадки
 * учётки сюда не приезжают вовсе — в карточке человека их нет в `UserAccountDto`, а во вкладке
 * профилей строкой стоит роль, а не учётка. Поэтому правило, которое внутри одной роли зависит от
 * её области (спецтехника у отдела — ADR 0201), пишется ВЕРНЫМ ПРИ ОБОИХ СОСТОЯНИЯХ, а не сужается
 * вторым предикатом: текст «только грузоперевозки», показанный отделу с площадкой, врёт ровно там,
 * где по витрине принимают решение, — а показать он его обязан, площадок-то он не видит.
 */
export interface AccessScopeRule {
  applies: (subject: AccessSubject) => boolean;
  text: string;
}

const SCOPE_RULES: readonly AccessScopeRule[] = [
  {
    applies: (s) => isObjectScopedRole(s.role),
    text: 'Видит только записи своих объектов',
  },
  {
    applies: (s) => isDepartmentScopedRole(s.role),
    text: 'Видит заявки своих отделов; вывоз мусора, механизацию и заказ техники — ещё и по площадкам этих отделов',
  },
  {
    applies: (s) => isDepartmentScopedRole(s.role),
    text: 'Заказывает грузоперевозки всегда, спецтехнику — пока за его отделами закреплена хоть одна площадка',
  },
  {
    applies: (s) => isObjectScopedRole(s.role) || isDepartmentScopedRole(s.role),
    text: 'Правит и удаляет заявку только в статусе «Новая»',
  },
  {
    applies: (s) => isCounterpartyScopedRole(s.role) && s.counterpartyType === 'operator',
    text: 'Видит только заявки на вывоз, назначенные его контрагенту',
  },
  {
    applies: (s) => isCounterpartyScopedRole(s.role) && s.counterpartyType === 'vehicle_lessor',
    text: 'Видит только заказы, на которые назначена его техника; «Новую» заявку — нет',
  },
  {
    applies: (s) => isCounterpartyScopedRole(s.role) && s.counterpartyType === 'service',
    text: 'Видит только заявки на обслуживание, назначенные его компании',
  },
  /**
   * Четвёртая ось — работник справочника (ADR 0102 §5), и она самая узкая из четырёх. Правило здесь
   * такое же текстовое, как соседние, хотя предиката в `lib/access.ts` у оси нет: область держится
   * тем, что кабинет не принимает `personId` вовсе. Витрине от этого не легче — не скажи она про
   * область ничего, водитель показался бы видящим всё, что открывают его права.
   */
  {
    applies: (s) => isPersonScopedRole(s.role),
    text: 'Видит только своё задание и свои показания: другого работника кабинет не спрашивает',
  },
  {
    applies: (s) => !!s.role && !can(s, 'archive.read'),
    text: 'Удалённые записи недоступны: по прямой ссылке — «не найдено»',
  },
];

/** Область не ограничена ничем: роль без своей оси и с доступом к архиву (сегодня — администратор). */
export const UNSCOPED_TEXT = 'Областью не ограничен: видит записи всех объектов и отделов';

export function describeAccessScope(subject: AccessSubject | null | undefined): readonly string[] {
  if (!subject?.role) return ['Роль не назначена: доступа в портал нет'];
  const rules = SCOPE_RULES.filter((rule) => rule.applies(subject)).map((rule) => rule.text);
  return rules.length > 0 ? rules : [UNSCOPED_TEXT];
}

/**
 * Роли, у которых область задаётся набором в учётке, — им витрина показывает сам набор. Осей
 * четыре, ровно по спискам ролей из `enums.ts`: объекты, отделы, контрагент и работник справочника
 * (ADR 0102). `null` означает «своей оси у роли нет», и пропущенная здесь ось превращается в это
 * же `null` — то есть в витрину, которая печатает учётке «Все записи» вместо её области.
 */
export function scopeAxisOf(
  role: Role | null | undefined,
): 'object' | 'department' | 'counterparty' | 'person' | null {
  if (isObjectScopedRole(role)) return 'object';
  if (isDepartmentScopedRole(role)) return 'department';
  if (isCounterpartyScopedRole(role)) return 'counterparty';
  if (isPersonScopedRole(role)) return 'person';
  return null;
}
